import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {LimitAndOffset} from "@/components/table/subComponents/Paginator/Paginator.tsx";
import {TableEmptyStateCta} from "@/components/table/subComponents/TableEmptyStateCta/TableEmptyStateCta.tsx";
import {Table} from "@/components/table/Table.tsx";
import {ColumnSpec} from "@/components/table/Table.types.ts";
import {Tooltip} from "@/components/toolTip/Tooltip.tsx";
import {WHATSAPP_CONTACT} from "@/lib/featureFlags.ts";
import {ContactGroup} from "@/lib/types/contacts.ts";
import {IntegrationProfile} from "@/pages/contacts/components/form/types.ts";
import {ContactQuickActionsBar} from "@/pages/contacts/list/actions/ContactQuickActionsBar.tsx";
import {CreateContactAction} from "@/pages/contacts/list/actions/CreateContactAction.tsx";
import {ListCell} from "@/pages/contacts/list/components/table/ListCell.tsx";
import {ScheduleIconCell} from "@/pages/contacts/list/components/table/ScheduleIconCell.tsx";

import styles from "./ContactGroupsTable.module.scss";

type ContactGroupsTableProps = {
  items: ContactGroup[];
  integrationProfiles: IntegrationProfile[];
  hasWriteAccess?: boolean;
  isFiltered?: boolean;
  pagination?: LimitAndOffset;
  totalRows?: number;
  updatePagination?: (laf: LimitAndOffset) => void;
};

export const ContactGroupsTable = ({
  items,
  hasWriteAccess,
  isFiltered,
  integrationProfiles,
  pagination,
  totalRows,
  updatePagination,
}: ContactGroupsTableProps) => {
  const {userHasFeature} = useCurrentUserContext();
  const contactMethodsTitle = userHasFeature(WHATSAPP_CONTACT) ? "Email / Phone / WhatsApp" : "Email / Phone";
  const integrationsMap = integrationProfiles.reduce(
    (result, {id, name}) => {
      result[id] = name;
      return result;
    },
    {} as Record<number, string>
  );
  const specs: ColumnSpec<ContactGroup>[] = [
    {
      fieldName: "name",
      fieldTitle: "Name",
      meta: {headerClassName: styles.nameCell},
      accessor: ({name}) => (
        <Tooltip tooltipContent={name} delayMs={1000} onModal>
          <div className="ellipsis-overflow">{name}</div>
        </Tooltip>
      ),
    },
    {
      fieldName: "schedule",
      meta: {headerClassName: styles.scheduleCell},
      accessor: ({schedule_state}) => <ScheduleIconCell scheduleState={schedule_state} />,
    },
    {
      fieldName: "emailPhone",
      fieldTitle: contactMethodsTitle,
      meta: {headerClassName: styles.contactMethodsCell},
      accessor: ({id, email_list, sms_list, phonecall_list, whatsapp_list}) => (
        <>
          <ListCell id={`email-list-${id}`} items={email_list} />
          <ListCell id={`phone-list-${id}`} items={sms_list.concat(phonecall_list, whatsapp_list)} phoneNumber />
        </>
      ),
    },
    {
      fieldName: "integrations",
      fieldTitle: "Integrations",
      meta: {headerClassName: styles.integrationsCell},
      accessor: ({id, integrations}) => (
        <ListCell
          id={`contact-integration-${id}`}
          items={integrations.map((integrationId) => integrationsMap[integrationId])}
        />
      ),
    },
    {
      fieldName: "numChecks",
      fieldTitle: "Checks",
      meta: {headerClassName: styles.checksCell},
      accessor: ({num_services}) => num_services,
    },
    {
      fieldName: "numEscalations",
      fieldTitle: "Escalations",
      meta: {headerClassName: styles.escalationsCell},
      accessor: ({num_escalations}) => num_escalations,
    },
    {
      fieldName: "actions",
      meta: {headerClassName: styles.actionsCell},
      accessor: (obj) => <ContactQuickActionsBar contact={obj} hasWriteAccess={hasWriteAccess === true} />,
    },
  ];

  return (
    <Table
      fixedWidth
      minWidth={"800px"}
      rows={items}
      specs={specs}
      initialState={{columnPinning: {right: ["actions"]}}}
      pagination={pagination}
      totalRows={totalRows}
      updatePagination={updatePagination}
      noDataComponent={
        <TableEmptyStateCta
          colSpan={specs.length}
          title={isFiltered ? "No Contacts found matching the filter(s) above." : "You do not have any Contacts."}
          cta={!isFiltered && hasWriteAccess ? <CreateContactAction variant="secondary" /> : undefined}
        />
      }
    />
  );
};
